import { useCallback } from "react";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import { UploadElementType } from "@custom-types/file-upload-types";
import { EventProps } from "@faro-lotv/foreign-observers";
import { UploadSettings } from "@hooks/data-management/use-upload-multiple-scans";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ZipFile } from "@utils/zip-file";

type ReturnFunction = (
  customProps: EventProps,
  files: (File | ZipFile)[],
  elementType: UploadElementType,
  uploadSettings?: UploadSettings,
) => void;

/** Tracks an Amplitude event for a started file upload. */
export function useUploadFilesTrackStart(): ReturnFunction {
  const { trackEvent } = useTrackEvent();
  const { uploadManager } = useFileUploadContext();

  return useCallback(
    (
      customProps,
      files,
      elementType,
      uploadSettings
    ): void => {
      const props: EventProps = {
        type: elementType,
        files: files.length,
        filesToUpload: files.length,
        filesToUploadSize: files.reduce((sum, file) => sum + file.size, 0),
        isSharedWorker: uploadManager.isSharedWorker(),
        ...customProps,
      };
      if (uploadSettings) {
        props.workflowType = uploadSettings.workflowType;
        props.shouldGeneratePointCloud = uploadSettings.shouldGeneratePointCloud;
        props.shouldApplyReflectionFilters = uploadSettings.shouldApplyReflectionFilters;
      }

      trackEvent({
        name: DataManagementEvents.startUpload,
        props,
      });
    },
    [trackEvent, uploadManager]
  );
}
